import PageElement from "ui/PageElement";
import Spinner from "ui/components/spinner/Spinner";
import FatalError from "ui/components/fatal-error/FatalError";
import i18n from "ui/handlebars-helpers/i18n";
import List from "ui/elements/list/List";
import Ajax from "ui/helpers/Ajax";

const RECORDS_ON_PAGE = 40;

export default class AbstractList extends PageElement {
	/**
	 * @param {T_PageElementOptions} [options]
	 */
	constructor(options = {}) {
		super(options);

		/**
		 * @type {string}
		 * @protected
		 */
		this._sortBy = 'create';

		/**
		 * @type {boolean}
		 * @protected
		 */
		this._sortAsc = false;

		/**
		 * @type {number}
		 * @protected
		 */
		this._page = 1;

		/**
		 * @type {boolean}
		 * @protected
		 */
		this._isEOF = false;

		/**
		 * @type {Array<T_UserInList>}
		 * @protected
		 */
		this._data = [];

		/**
		 * @type {boolean}
		 * @protected
		 */
		this._searchInProgress = false;

		/**
		 * @type {List}
		 * @protected
		 */
		this._list = null;

		/**
		 * @type {boolean}
		 * @private
		 */
		this._needReload = false;

		/**
		 * @type {number}
		 * @private
		 */
		this._needTimer = 0;

		/**
		 * @type {boolean}
		 * @private
		 */
		this._isLoading = false;
	}

	deactivatePageElement() {
		super.deactivatePageElement();
		this._needTimer = setTimeout(() => {
			this._needReload = true;
		}, 2000);
	}

	activatePageElement() {
		super.activatePageElement();
		clearTimeout(this._needTimer);
		if (this._needReload) {
			this._needReload = false;
			this.refresh().then();
		}
	}

	async refresh() {
		if (this._searchInProgress) {
			return;
		}

		this._searchInProgress = true;
		this._resetPage();
		await this._loadData(true);
		this._searchInProgress = false;
	}

	destroy() {
		super.destroy();
		clearTimeout(this._needTimer);
	}

	/**
	 * @return {string}
	 * @protected
	 */
	_getRequestUrl() {
		return '';
	}

	/**
	 * @param {*} data
	 * @protected
	 */
	_handleResponse(data) {
	}

	/**
	 * @return {object}
	 * @protected
	 */
	_getSearchParams() {
		return {};
	}

	/**
	 * @return {T_ListOptions}
	 * @protected
	 */
	_getListOptions() {
		return {
			columns: []
		};
	}

	/**
	 * @param {string} role
	 * @param {object} row
	 * @return {Promise<void>}
	 * @protected
	 */
	async _handleButton(role, row) {
		// for overriding
	}

	/**
	 * @protected
	 */
	async _build() {
		this._els = Object.assign(this._els || {}, {
			searchForm: this._element.querySelector('form'),
			searchText: this._element.querySelector('input[data-role="text"]'),
			count: this._element.querySelector('.list__count span'),
		});

		this._prepareSearchForm();
		this._prepareTopButtons();
		this._buildList();
		await this._loadData();
	}

	/**
	 * @protected
	 */
	_prepareTopButtons() {
		this._element.querySelector('.button[data-role="refresh"]').onclick = async () => {
			await this.refresh();
		};
	}

	/**
	 * @protected
	 * @returns {void}
	 */
	_prepareSearchForm() {
		this._els.searchText && this._els.searchText.addEventListener('change', () => {
			this.refresh().then();
		});

		this._els.searchForm && this._els.searchForm.addEventListener('submit', (e) => {
			e.preventDefault();
			this.refresh().then();
		});
	}

	/**
	 * @param {boolean} [showLoader]
	 * @returns {Promise<void>}
	 * @private
	 */
	async _loadData(showLoader = false) {
		if (this._isEOF) {
			return;
		}

		this._isLoading = true;

		if (showLoader) {
			Spinner.show();
		}

		let text = this._els.searchText ? this._els.searchText.value : '';

		let res;
		try {
			res = await Ajax.post(this._getRequestUrl(), Object.assign(this._getSearchParams(), {
				text,
				page: this._page,
				sort: this._sortBy,
				sortType: this._sortAsc ? 'asc' : 'desc'
			}));
		} catch (e) {
			this._isEOF = true;
			this._isLoading = false;
			if (showLoader) {
				Spinner.hide();
			}

			FatalError.show(typeof e === 'string' ? e : i18n('errors.http.server-issue'));
			return;
		}

		this._handleResponse(res);
		this._els.count.textContent = String(res.count);

		if (res.list.length > 0) {
			this._data.push(...res.list);
			this._list.appendData(res.list);
			if (res.list.length < RECORDS_ON_PAGE) {
				this._isEOF = true;
				this._list.finishList();
			}
		} else {
			this._isEOF = true;
			this._list.finishList();
		}

		if (showLoader) {
			Spinner.hide();
		}

		this._isLoading = false;
	}

	/**
	 * @private
	 * @returns {void}
	 */
	_buildList() {
		this._list = new List(Object.assign({
			data: this._data,
			buttonsHandler: this._onButtonClick.bind(this),
			headerHandler: this._onHeaderClick.bind(this),
			nextPageHandler: this._loadNextPage.bind(this),
			parent: this._element,
		}, this._getListOptions()));
	}

	/**
	 * @param {Element} button
	 * @param {object} data
	 * @returns {Promise<void>}
	 * @private
	 */
	async _onButtonClick(button, data) {
		if (!button.getAttribute('data-role')) {
			button = button.querySelector('[data-role]');
			if (!button || !button.getAttribute('data-role')) {
				return;
			}
		}

		this._handleButton(button.getAttribute('data-role'), data);
	}

	/**
	 * @return {boolean}
	 * @private
	 */
	_loadNextPage() {
		if (this._isEOF) {
			return false;
		}

		if (this._isLoading) {
			return true;
		}

		this._page++;
		this._loadData(false).then();

		return true;
	}

	/**
	 * @param {string} key
	 * @param {boolean} asc
	 * @returns {void}
	 * @private
	 */
	_onHeaderClick(key, asc) {
		this._sortBy = key;
		this._sortAsc = asc;
		this._resetPage();
		void this._loadData(true);
	}

	/**
	 * @private
	 */
	_resetPage() {
		this._list.reset();
		this._data = [];
		this._page = 1;
		this._isEOF = false;
	}
}
